import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ChevronLeft, ChevronRight, Plus, Trash2, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconTile, useIconUrlMap } from "./IconTile";
import {
  type FormFactor,
  type HomeScreenLayout,
  type IconItem,
  type IconKind,
  SPECS,
  pageCapacity,
  MAX_HOME_PAGES,
} from "./types";
import { cn } from "@/lib/utils";

interface DeviceCanvasProps {
  ff: FormFactor;
  layout: HomeScreenLayout;
  pageIndex: number;
  onPageChange: (i: number) => void;
  onAddPage: () => void;
  onRemovePage: () => void;
  onMovePage?: (from: number, to: number) => void;
  onSelect: (path: SelectionPath | null) => void;
  selected: SelectionPath | null;
  onOpenFolder: (folderIndex: number) => void;
  scale?: number;
  onWebClipIconKindResolved?: (url: string, kind: IconKind) => void;
}

export type SelectionPath =
  | { kind: "page"; pageIndex: number; itemIndex: number }
  | { kind: "dock"; itemIndex: number };

function CellDroppable({
  id,
  children,
  className,
}: {
  id: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-xl flex items-center justify-center transition-colors",
        isOver && "bg-primary/15 ring-2 ring-primary",
        className
      )}
    >
      {children}
    </div>
  );
}

function MovableTile({
  from,
  children,
}: {
  from: SelectionPath;
  children: React.ReactNode;
}) {
  const id = `move:${from.kind === "dock" ? `dock:${from.itemIndex}` : `page:${from.pageIndex}:${from.itemIndex}`}`;
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id,
    data: { kind: "move", from },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn("touch-none", isDragging && "opacity-40")}
    >
      {children}
    </div>
  );
}

export function DeviceCanvas(props: DeviceCanvasProps) {
  const { ff, layout, pageIndex, onPageChange, onAddPage, onRemovePage, onMovePage, onSelect, selected, onOpenFolder, scale = 1, onWebClipIconKindResolved } = props;
  const spec = SPECS[ff];
  const cap = pageCapacity(ff);
  const page = layout.Pages[pageIndex] ?? [];
  const dock = layout.Dock ?? [];

  const allItems = [...layout.Pages.flat(), ...dock];
  const iconMap = useIconUrlMap(allItems);

  const totalPages = layout.Pages.length;

  const frameWidth = ff === "iphone" ? 280 : 420;
  const frameHeight = ff === "iphone" ? 560 : 540;

  return (
    <div className="flex flex-col items-center gap-3" style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{spec.label}</div>

      {/* Device frame */}
      <div
        className="relative bg-gradient-to-b from-muted/40 to-muted/10 rounded-[36px] border-4 border-foreground/10 shadow-lg p-4 flex flex-col"
        style={{ width: frameWidth, height: frameHeight }}
      >
        {/* Notch / Dynamic Island */}
        {ff === "iphone" && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-20 rounded-full bg-foreground/80" aria-hidden />
        )}

        {/* Page grid */}
        <div className="flex-1 mt-4 mb-2">
          <div
            className="grid gap-1 h-full"
            style={{
              gridTemplateColumns: `repeat(${spec.grid.cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${spec.grid.rows}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: cap }).map((_, i) => {
              const item = page[i];
              const cellId = `cell:page:${pageIndex}:${i}`;
              const isSelected =
                selected?.kind === "page" &&
                selected.pageIndex === pageIndex &&
                selected.itemIndex === i;
              return (
                <CellDroppable key={cellId} id={cellId} className="aspect-square">
                  {item ? (
                    <MovableTile from={{ kind: "page", pageIndex, itemIndex: i }}>
                      <IconTile
                        item={item}
                        iconUrl={item.Type === "Application" ? iconMap[item.BundleID] : undefined}
                        size="sm"
                        selected={isSelected}
                        onWebClipIconKindResolved={onWebClipIconKindResolved}
                        onClick={() => {
                          if (item.Type === "Folder") {
                            onOpenFolder(i);
                          } else {
                            onSelect({ kind: "page", pageIndex, itemIndex: i });
                          }
                        }}
                      />
                    </MovableTile>
                  ) : null}
                </CellDroppable>
              );
            })}
          </div>
        </div>

        {/* Dock */}
        <div className="rounded-2xl bg-foreground/5 backdrop-blur p-2">
          <div
            className="grid gap-1"
            style={{ gridTemplateColumns: `repeat(${spec.dockMax}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: spec.dockMax }).map((_, i) => {
              const item = dock[i];
              const cellId = `cell:dock:${i}`;
              const isSelected = selected?.kind === "dock" && selected.itemIndex === i;
              return (
                <CellDroppable key={cellId} id={cellId} className="aspect-square">
                  {item ? (
                    <MovableTile from={{ kind: "dock", itemIndex: i }}>
                      <IconTile
                        item={item}
                        iconUrl={item.Type === "Application" ? iconMap[item.BundleID] : undefined}
                        size="sm"
                        hideLabel
                        selected={isSelected}
                        onWebClipIconKindResolved={onWebClipIconKindResolved}
                        onClick={() => onSelect({ kind: "dock", itemIndex: i })}
                      />
                    </MovableTile>
                  ) : null}
                </CellDroppable>
              );
            })}
          </div>
        </div>
      </div>

      {/* Page navigator */}
      <div className="flex items-center gap-2">
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
          disabled={pageIndex === 0}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPageChange(i)}
              className={cn(
                "h-2 w-2 rounded-full transition-colors",
                i === pageIndex ? "bg-foreground" : "bg-muted-foreground/40 hover:bg-muted-foreground/70"
              )}
              aria-label={`Go to page ${i + 1}`}
              aria-current={i === pageIndex}
            />
          ))}
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => onPageChange(Math.min(totalPages - 1, pageIndex + 1))}
          disabled={pageIndex >= totalPages - 1}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Reorder current page */}
      {onMovePage && totalPages > 1 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ArrowLeftRight className="h-3.5 w-3.5" />
          <span>Reorder page {pageIndex + 1}</span>
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7"
            onClick={() => onMovePage(pageIndex, pageIndex - 1)}
            disabled={pageIndex === 0}
            aria-label="Move page left"
            title="Move page left"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7"
            onClick={() => onMovePage(pageIndex, pageIndex + 1)}
            disabled={pageIndex >= totalPages - 1}
            aria-label="Move page right"
            title="Move page right"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onAddPage}
          disabled={totalPages >= MAX_HOME_PAGES}
        >
          <Plus className="h-4 w-4 mr-1" /> Add page
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onRemovePage}
          disabled={totalPages <= 1 || page.length > 0}
          title={page.length > 0 ? "Remove icons before deleting page" : ""}
        >
          <Trash2 className="h-4 w-4 mr-1" /> Remove page
        </Button>
      </div>
    </div>
  );
}
